import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Grid, Chip,
  Select, MenuItem, FormControl, InputLabel, Switch, FormControlLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, Skeleton, Tooltip, Divider, Slider,
} from '@mui/material';
import {
  Add, Delete, Edit, ToggleOn, ToggleOff, Notifications,
  NotificationsOff, Email, Wifi, History,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { alertsAPI } from '../services/api';
import { useWebSocket } from '../hooks/useWebSocket';
import EventTypeChip from '../components/common/EventTypeChip';
import SeverityChip from '../components/common/SeverityChip';
import SatellitePassAlerts from '../components/space/SatellitePassAlerts';

const EVENT_TYPES = [
  'SOLAR_FLARE', 'GEOMAGNETIC_STORM', 'CME',
  'RADIATION_BELT', 'SOLAR_ENERGETIC_PARTICLE', 'INTERPLANETARY_SHOCK',
];

const SEVERITIES = ['LOW', 'MODERATE', 'HIGH', 'EXTREME'];

const FLARE_CLASSES = [
  { value: 'C', label: 'C-class (Moderate)', color: '#ffd600' },
  { value: 'M', label: 'M-class (Strong)',   color: '#ff9800' },
  { value: 'X', label: 'X-class (Extreme)',  color: '#f44336' },
];

const EMPTY_FORM = {
  eventType: 'SOLAR_FLARE',
  thresholdSeverity: 'HIGH',
  flareClassThreshold: 'M',
  kpIndexThreshold: 5,
  earthDirectedOnly: false,
  notifyEmail: true,
  notifyWebsocket: true,
};

const ASTROTRACK_ALERTS = [
  { id: 'meteor',    icon: '☄️', title: 'Meteor Shower',        desc: 'Perseid, Geminid, Leonid and other major meteor showers',   color: '#ffa726' },
  { id: 'fullmoon',  icon: '🌕', title: 'Full Moon',            desc: 'Monthly full moon and supermoon notifications',             color: '#f8bbd0' },
  { id: 'eclipse',   icon: '🌑', title: 'Solar/Lunar Eclipse',  desc: 'Total, partial and penumbral eclipse alerts',               color: '#ef5350' },
  { id: 'planet',    icon: '🪐', title: 'Planet Opposition',    desc: 'Mars, Jupiter, Saturn at closest approach to Earth',        color: '#c9a96e' },
  { id: 'alignment', icon: '✨', title: 'Planetary Alignment',  desc: 'Rare planetary conjunctions and alignments',                color: '#7e57c2' },
  { id: 'solstice',  icon: '☀️', title: 'Solstice & Equinox',   desc: 'Summer/winter solstice and vernal/autumnal equinox',        color: '#ffd600' },
  { id: 'supermoon', icon: '🌕', title: 'Supermoon',            desc: 'Moon at perigee — 14% bigger and 30% brighter than normal', color: '#81d4fa' },
  { id: 'iss',       icon: '🛸', title: 'ISS Overhead Pass',    desc: 'International Space Station visible from your location',    color: '#ffffff' },
  { id: 'aurora',    icon: '🌌', title: 'Aurora Borealis',      desc: 'Northern/Southern Lights visibility alerts',                color: '#66bb6a' },
  { id: 'comet',     icon: '🌠', title: 'Comet Visibility',     desc: 'Visible comets and newly discovered celestial objects',     color: '#ab47bc' },
];

// ─── AstroAlertCard Component ─────────────────────────────────────────────────
function AstroAlertCard({ alert }) {
  const [subscribed, setSubscribed] = useState(false);
  const [emailOn, setEmailOn]       = useState(true);
  const [pushOn, setPushOn]         = useState(true);

  return (
    <Card sx={{
      height: '100%',
      border: subscribed ? `1px solid ${alert.color}` : `1px solid ${alert.color}33`,
      transition: 'all 0.2s',
      position: 'relative', overflow: 'hidden',
      '&::before': {
        content: '""', position: 'absolute',
        top: 0, left: 0, right: 0, height: 3,
        bgcolor: alert.color,
        opacity: subscribed ? 1 : 0.4,
      },
      '&:hover': { border: `1px solid ${alert.color}88` },
    }}>
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: 26 }}>{alert.icon}</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: alert.color }}>
              {alert.title}
            </Typography>
          </Box>
          <Chip
            label={subscribed ? 'ON' : 'OFF'}
            size="small"
            onClick={() => setSubscribed(s => !s)}
            sx={{
              cursor: 'pointer', fontWeight: 800, fontSize: 10,
              bgcolor: subscribed ? `${alert.color}33` : 'rgba(84,84,84,0.2)',
              color:   subscribed ? alert.color : 'text.secondary',
              border:  subscribed ? `1px solid ${alert.color}` : '1px solid rgba(84,84,84,0.4)',
            }}
          />
        </Box>
        <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.6, display: 'block', mb: 1.5 }}>
          {alert.desc}
        </Typography>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mb: 1.5 }} />
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            size="small"
            icon={<Email sx={{ fontSize: '12px !important' }} />}
            label="Email"
            onClick={() => subscribed && setEmailOn(e => !e)}
            sx={{
              cursor: subscribed ? 'pointer' : 'default',
              opacity: subscribed ? 1 : 0.4,
              bgcolor: subscribed && emailOn ? 'rgba(255,255,255,0.15)' : 'rgba(84,84,84,0.1)',
              color:   subscribed && emailOn ? 'primary.light' : 'text.secondary',
              border:  subscribed && emailOn ? '1px solid rgba(255,255,255,0.4)' : '1px solid transparent',
              fontSize: 10,
            }}
          />
          <Chip
            size="small"
            icon={<Wifi sx={{ fontSize: '12px !important' }} />}
            label="Live"
            onClick={() => subscribed && setPushOn(p => !p)}
            sx={{
              cursor: subscribed ? 'pointer' : 'default',
              opacity: subscribed ? 1 : 0.4,
              bgcolor: subscribed && pushOn ? 'rgba(102,187,106,0.15)' : 'rgba(84,84,84,0.1)',
              color:   subscribed && pushOn ? 'success.main' : 'text.secondary',
              border:  subscribed && pushOn ? '1px solid rgba(102,187,106,0.4)' : '1px solid transparent',
              fontSize: 10,
            }}
          />
          {subscribed && (
            <Chip size="small" label="✓ Active"
              sx={{ bgcolor: 'rgba(102,187,106,0.1)', color: '#66bb6a',
                border: '1px solid rgba(102,187,106,0.3)', fontSize: 10, fontWeight: 700 }} />
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

// ─── Per-event-type threshold hint ───────────────────────────────────────────
function buildThresholdSummary(form) {
  const type = form.eventType;
  if (type === 'SOLAR_FLARE' && form.flareClassThreshold) {
    return <>Alert when a <strong>Solar Flare</strong> is <strong>{form.flareClassThreshold}-class or higher</strong> (severity ≥ {form.thresholdSeverity})</>;
  }
  if (type === 'GEOMAGNETIC_STORM' && form.kpIndexThreshold) {
    return <>Alert when <strong>Geomagnetic Storm</strong> Kp-index reaches <strong>≥ {form.kpIndexThreshold}</strong> (severity ≥ {form.thresholdSeverity})</>;
  }
  if (type === 'CME' && form.earthDirectedOnly) {
    return <>Alert only for <strong>Earth-directed CMEs</strong> at severity ≥ {form.thresholdSeverity}</>;
  }
  return <>Alert when a <strong>{type.replace(/_/g, ' ')}</strong> event reaches <strong>{form.thresholdSeverity}</strong> severity or higher</>;
}

// ─── Severity color helper ────────────────────────────────────────────────────
const SEVERITY_COLOR = { LOW: '#4caf50', MODERATE: '#ff9800', HIGH: '#f44336', EXTREME: '#9c27b0', UNKNOWN: '#9e9e9e' };

// ─── Main AlertsPage ──────────────────────────────────────────────────────────
export default function AlertsPage() {
  const [alerts, setAlerts]     = useState([]);
  const [history, setHistory]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [histLoading, setHistLoading] = useState(false);
  const [open, setOpen]         = useState(false);
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [saving, setSaving]     = useState(false);
  const [msg, setMsg]           = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await alertsAPI.getAlerts();
      setAlerts(res.data.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  const loadHistory = useCallback(async () => {
    setHistLoading(true);
    try {
      const res = await alertsAPI.getHistory();
      setHistory(res.data.data || []);
    } catch (err) { console.error(err); }
    finally { setHistLoading(false); }
  }, []);

  useEffect(() => { load(); loadHistory(); }, [load, loadHistory]);

  const handleSpaceEvent = useCallback((event) => {
    if (event.alertId) {
      setAlerts(prev => prev.filter(a => a.id !== event.alertId));
      loadHistory();
    }
  }, [loadHistory]);

  useWebSocket(handleSpaceEvent);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setOpen(true); };
  const openEdit   = (alert) => {
    setEditing(alert);
    setForm({
      eventType: alert.eventType,
      thresholdSeverity: alert.thresholdSeverity,
      flareClassThreshold: alert.flareClassThreshold || 'M',
      kpIndexThreshold: alert.kpIndexThreshold ?? 5,
      earthDirectedOnly: alert.earthDirectedOnly ?? false,
      notifyEmail: alert.notifyEmail,
      notifyWebsocket: alert.notifyWebsocket,
    });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Only send type-specific fields relevant to the selected event type
      const payload = {
        eventType: form.eventType,
        thresholdSeverity: form.thresholdSeverity,
        notifyEmail: form.notifyEmail,
        notifyWebsocket: form.notifyWebsocket,
        flareClassThreshold: form.eventType === 'SOLAR_FLARE' ? form.flareClassThreshold : null,
        kpIndexThreshold: form.eventType === 'GEOMAGNETIC_STORM' ? form.kpIndexThreshold : null,
        earthDirectedOnly: form.eventType === 'CME' ? form.earthDirectedOnly : false,
      };

      if (editing) {
        await alertsAPI.updateAlert(editing.id, payload);
        setMsg('Alert updated.');
      } else {
        await alertsAPI.createAlert(payload);
        setMsg('Alert created.');
      }
      setOpen(false);
      load();
      loadHistory();
    } catch (err) {
      setMsg(err.response?.data?.message || 'Save failed.');
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(''), 3000);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this alert?')) return;
    await alertsAPI.deleteAlert(id);
    setAlerts(a => a.filter(x => x.id !== id));
    setMsg('Alert deleted.');
    setTimeout(() => setMsg(''), 3000);
  };

  const handleToggle = async (id) => {
    await alertsAPI.toggleAlert(id);
    load();
  };

  // Extra threshold label shown in table
  const thresholdDetail = (alert) => {
    if (alert.eventType === 'SOLAR_FLARE' && alert.flareClassThreshold) {
      return <Chip size="small" label={`≥ ${alert.flareClassThreshold}-class`}
        sx={{ fontSize: 10, ml: 0.5, bgcolor: 'rgba(255,214,0,0.1)', color: '#ffd600', border: '1px solid rgba(255,214,0,0.3)' }} />;
    }
    if (alert.eventType === 'GEOMAGNETIC_STORM' && alert.kpIndexThreshold != null) {
      return <Chip size="small" label={`Kp ≥ ${alert.kpIndexThreshold}`}
        sx={{ fontSize: 10, ml: 0.5, bgcolor: 'rgba(33,150,243,0.1)', color: '#42a5f5', border: '1px solid rgba(33,150,243,0.3)' }} />;
    }
    if (alert.eventType === 'CME' && alert.earthDirectedOnly) {
      return <Chip size="small" label="Earth-directed"
        sx={{ fontSize: 10, ml: 0.5, bgcolor: 'rgba(76,175,80,0.1)', color: '#66bb6a', border: '1px solid rgba(76,175,80,0.3)' }} />;
    }
    return null;
  };

  return (
    <Box>

      {/* ── Page Header ─────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>Alert Settings</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            Get notified when space weather exceeds your thresholds
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
          New Alert
        </Button>
      </Box>

      {msg && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>{msg}</Alert>}

      {/* ── How Alerts Work ──────────────────────────────────────────────── */}
      <Card sx={{ mb: 3, background: 'rgba(2,136,209,0.06)', border: '1px solid rgba(255,255,255,0.2)' }}>
        <CardContent sx={{ p: 2.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.light', mb: 1 }}>
            How Alerts Work
          </Typography>
          <Grid container spacing={2}>
            {[
              { icon: <Notifications sx={{ color: 'primary.main', fontSize: 20 }} />, title: 'Real-time Alerts',     desc: 'Instant browser notifications via WebSocket when new events arrive' },
              { icon: <Email         sx={{ color: 'primary.main', fontSize: 20 }} />, title: 'Email Notifications',  desc: 'Rich HTML emails with event details sent to your registered address' },
              { icon: <Wifi          sx={{ color: 'primary.main', fontSize: 20 }} />, title: 'Custom Thresholds',    desc: 'Set flare class, Kp-index, or Earth-directed filters per event type' },
            ].map(({ icon, title, desc }) => (
              <Grid item xs={12} sm={4} key={title}>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Box sx={{ mt: 0.3, flexShrink: 0 }}>{icon}</Box>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>{title}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{desc}</Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* ── Space Weather Alerts Table ───────────────────────────────────── */}
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
        ⚡ Space Weather Alerts
      </Typography>

      <TableContainer component={Paper} sx={{ bgcolor: 'background.paper', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2, mb: 4 }}>
        <Table>
          <TableHead>
            <TableRow>
              {['Event Type', 'Min Severity', 'Custom Threshold', 'Notifications', 'Status', 'Last Triggered', 'Actions'].map(h => (
                <TableCell key={h} sx={{ color: 'text.secondary', fontWeight: 700, fontSize: 11,
                  textTransform: 'uppercase', letterSpacing: 0.5,
                  borderBottom: '1px solid rgba(255,255,255,0.1)' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading
              ? Array(3).fill(0).map((_, i) => (
                  <TableRow key={i}>
                    {Array(7).fill(0).map((_, j) => (
                      <TableCell key={j}><Skeleton sx={{ bgcolor: 'rgba(255,255,255,0.05)' }} /></TableCell>
                    ))}
                  </TableRow>
                ))
              : alerts.length === 0
                ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                      <NotificationsOff sx={{ fontSize: 40, opacity: 0.3, mb: 1, display: 'block', mx: 'auto' }} />
                      No alerts configured yet. Click "New Alert" to get started.
                    </TableCell>
                  </TableRow>
                )
                : alerts.map(alert => (
                  <TableRow key={alert.id} sx={{ opacity: alert.isActive ? 1 : 0.5 }}>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)', py: 1.5 }}>
                      <EventTypeChip type={alert.eventType} />
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      <SeverityChip severity={alert.thresholdSeverity} />
                      <Typography component="span" variant="caption" sx={{ color: 'text.secondary', ml: 0.5 }}>
                        +
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      {thresholdDetail(alert) || (
                        <Typography variant="caption" sx={{ color: 'text.disabled' }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {alert.notifyEmail     && <Chip size="small" icon={<Email sx={{ fontSize: '12px !important' }} />} label="Email" sx={{ fontSize: 10, bgcolor: 'rgba(255,255,255,0.1)',   color: 'primary.light' }} />}
                        {alert.notifyWebsocket && <Chip size="small" icon={<Wifi  sx={{ fontSize: '12px !important' }} />} label="Live"  sx={{ fontSize: 10, bgcolor: 'rgba(102,187,106,0.1)', color: 'success.main'  }} />}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      <Chip size="small" label={alert.isActive ? 'Active' : 'Paused'}
                        sx={{ bgcolor: alert.isActive ? 'rgba(102,187,106,0.15)' : 'rgba(84,84,84,0.15)',
                          color: alert.isActive ? 'success.main' : 'text.secondary', fontWeight: 700, fontSize: 10 }} />
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)', color: 'text.secondary', fontSize: 12 }}>
                      {alert.lastTriggered ? format(new Date(alert.lastTriggered), 'MMM dd, HH:mm') : 'Never'}
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title={alert.isActive ? 'Pause' : 'Activate'}>
                          <IconButton size="small" onClick={() => handleToggle(alert.id)}
                            sx={{ color: alert.isActive ? 'success.main' : 'text.secondary' }}>
                            {alert.isActive ? <ToggleOn /> : <ToggleOff />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(alert)} sx={{ color: 'primary.main' }}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => handleDelete(alert.id)} sx={{ color: 'error.main' }}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
            }
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Alert History ────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <History sx={{ color: 'primary.main' }} />
        <Typography variant="h6" sx={{ fontWeight: 800 }}>Alert Trigger History</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>(last 10)</Typography>
      </Box>

      <TableContainer component={Paper} sx={{ bgcolor: 'background.paper', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2, mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Event', 'Severity', 'Detail', 'Notified via', 'Triggered At'].map(h => (
                <TableCell key={h} sx={{ color: 'text.secondary', fontWeight: 700, fontSize: 11,
                  textTransform: 'uppercase', letterSpacing: 0.5,
                  borderBottom: '1px solid rgba(255,255,255,0.1)' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {histLoading
              ? Array(3).fill(0).map((_, i) => (
                  <TableRow key={i}>
                    {Array(5).fill(0).map((_, j) => (
                      <TableCell key={j}><Skeleton sx={{ bgcolor: 'rgba(255,255,255,0.05)' }} /></TableCell>
                    ))}
                  </TableRow>
                ))
              : history.length === 0
                ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                      <History sx={{ fontSize: 36, opacity: 0.2, mb: 0.5, display: 'block', mx: 'auto' }} />
                      No alerts have been triggered yet.
                    </TableCell>
                  </TableRow>
                )
                : history.map(h => (
                  <TableRow key={h.id}>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)', py: 1.5 }}>
                      <EventTypeChip type={h.eventType} />
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      <Chip size="small" label={h.severity}
                        sx={{ fontSize: 10, fontWeight: 700,
                          bgcolor: `${SEVERITY_COLOR[h.severity] || '#9e9e9e'}22`,
                          color: SEVERITY_COLOR[h.severity] || '#9e9e9e',
                          border: `1px solid ${SEVERITY_COLOR[h.severity] || '#9e9e9e'}55` }} />
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)', color: 'text.secondary', fontSize: 12 }}>
                      {h.flareClass ? `Flare: ${h.flareClass}` : h.kpIndex != null ? `Kp: ${h.kpIndex}` : '—'}
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {h.notifiedEmail     && <Chip size="small" icon={<Email sx={{ fontSize: '12px !important' }} />} label="Email" sx={{ fontSize: 10, bgcolor: 'rgba(255,255,255,0.08)', color: 'primary.light' }} />}
                        {h.notifiedWebsocket && <Chip size="small" icon={<Wifi  sx={{ fontSize: '12px !important' }} />} label="Live"  sx={{ fontSize: 10, bgcolor: 'rgba(102,187,106,0.08)', color: 'success.main' }} />}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)', color: 'text.secondary', fontSize: 12 }}>
                      {format(new Date(h.triggeredAt), 'MMM dd, yyyy HH:mm')}
                    </TableCell>
                  </TableRow>
                ))
            }
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── ISS Pass Alerts ─────────────────────────────────────────────── */}
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
        🚀 ISS Pass Alerts
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
        Get an email ~20 minutes before the International Space Station passes over your location
      </Typography>
      <SatellitePassAlerts />

      <Box sx={{ mt: 4 }} />

      {/* ── AstroTrack Alerts ────────────────────────────────────────────── */}
      <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          🔭 AstroTrack Alerts
        </Typography>
        <Chip label="NEW" size="small"
          sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'primary.light',
            border: '1px solid rgba(255,255,255,0.4)', fontWeight: 800, fontSize: 10 }} />
      </Box>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Subscribe to astronomical event notifications from the AstroTrack Portal
      </Typography>

      <Grid container spacing={2}>
        {ASTROTRACK_ALERTS.map((alert) => (
          <Grid item xs={12} sm={6} md={4} key={alert.id}>
            <AstroAlertCard alert={alert} />
          </Grid>
        ))}
      </Grid>

      {/* ── Create / Edit Dialog ─────────────────────────────────────────── */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: 'background.paper', border: '1px solid rgba(255,255,255,0.2)' } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editing ? 'Edit Alert' : 'Create New Alert'}
        </DialogTitle>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            {buildThresholdSummary(form)}
          </Typography>
          <Grid container spacing={2}>

            {/* Event Type */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Event Type</InputLabel>
                <Select value={form.eventType} label="Event Type"
                  onChange={e => setForm(f => ({ ...f, eventType: e.target.value }))}>
                  {EVENT_TYPES.map(t => <MenuItem key={t} value={t}>{t.replace(/_/g, ' ')}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            {/* Severity */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Minimum Severity</InputLabel>
                <Select value={form.thresholdSeverity} label="Minimum Severity"
                  onChange={e => setForm(f => ({ ...f, thresholdSeverity: e.target.value }))}>
                  {SEVERITIES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            {/* Solar Flare: Flare Class */}
            {form.eventType === 'SOLAR_FLARE' && (
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                  Minimum Flare Class
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {FLARE_CLASSES.map(fc => (
                    <Chip
                      key={fc.value}
                      label={fc.label}
                      onClick={() => setForm(f => ({ ...f, flareClassThreshold: fc.value }))}
                      sx={{
                        cursor: 'pointer',
                        fontWeight: form.flareClassThreshold === fc.value ? 800 : 400,
                        bgcolor: form.flareClassThreshold === fc.value ? `${fc.color}22` : 'rgba(84,84,84,0.15)',
                        color: form.flareClassThreshold === fc.value ? fc.color : 'text.secondary',
                        border: form.flareClassThreshold === fc.value ? `1px solid ${fc.color}` : '1px solid rgba(84,84,84,0.3)',
                      }}
                    />
                  ))}
                </Box>
                <Typography variant="caption" sx={{ color: 'text.disabled', mt: 0.5, display: 'block' }}>
                  Only alert for flares of this class or stronger
                </Typography>
              </Grid>
            )}

            {/* Geomagnetic Storm: Kp-index */}
            {form.eventType === 'GEOMAGNETIC_STORM' && (
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                  Minimum Kp-index: <span style={{ color: '#42a5f5' }}>{form.kpIndexThreshold}</span>
                  <span style={{ color: '#777', fontSize: 11, marginLeft: 8 }}>
                    {form.kpIndexThreshold <= 3 ? '(Minor)' : form.kpIndexThreshold <= 5 ? '(Moderate)' : form.kpIndexThreshold <= 7 ? '(Strong)' : '(Severe)'}
                  </span>
                </Typography>
                <Slider
                  value={form.kpIndexThreshold}
                  min={1} max={9} step={1}
                  marks={[1,3,5,7,9].map(v => ({ value: v, label: String(v) }))}
                  onChange={(_, val) => setForm(f => ({ ...f, kpIndexThreshold: val }))}
                  sx={{ color: 'primary.main', mx: 1, width: 'calc(100% - 16px)' }}
                />
                <Typography variant="caption" sx={{ color: 'text.disabled', mt: 0.5, display: 'block' }}>
                  Kp 1–3: minor &nbsp;·&nbsp; 4–5: moderate &nbsp;·&nbsp; 6–7: strong &nbsp;·&nbsp; 8–9: severe/extreme
                </Typography>
              </Grid>
            )}

            {/* CME: Earth-directed only */}
            {form.eventType === 'CME' && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.earthDirectedOnly}
                      color="success"
                      onChange={e => setForm(f => ({ ...f, earthDirectedOnly: e.target.checked }))}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Earth-directed CMEs only</Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Only alert when the CME is heading toward Earth
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
            )}

            {/* Notification channels */}
            <Grid item xs={12}>
              <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mb: 1.5 }} />
              <Box sx={{ display: 'flex', gap: 3 }}>
                <FormControlLabel
                  control={<Switch checked={form.notifyEmail} color="primary"
                    onChange={e => setForm(f => ({ ...f, notifyEmail: e.target.checked }))} />}
                  label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Email fontSize="small" sx={{ color: 'primary.main' }} />
                    <Typography variant="body2">Email</Typography>
                  </Box>}
                />
                <FormControlLabel
                  control={<Switch checked={form.notifyWebsocket} color="success"
                    onChange={e => setForm(f => ({ ...f, notifyWebsocket: e.target.checked }))} />}
                  label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Wifi fontSize="small" sx={{ color: 'success.main' }} />
                    <Typography variant="body2">Live Alert</Typography>
                  </Box>}
                />
              </Box>
            </Grid>

          </Grid>
        </DialogContent>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setOpen(false)} sx={{ color: 'text.secondary' }}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : (editing ? 'Update Alert' : 'Create Alert')}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
